<template>
    <div style="width:100%; height:100%;">
        <side-menu-layout :is-default="false">

            <template slot="menu">
                <scrollable-page-layout
                    class="filtering-menu-con"
                    :no-top-offset="true"
                >
                    <slot name="view-switch"></slot>
                    <div class="groups-con">
                        <slot name="filter"></slot>
                    </div>
                </scrollable-page-layout>
            </template>

            <template #default>
                <scrollable-page-layout class="abstract-search-con">

                    <template slot="header">

                        <slot name="very-top"></slot>

                        <div class="top-bar-con">
                            <div class="flex-con is-grow xs-pad-lr top-bar-filters-con">
                                <slot name="top-bar-left"></slot>
                                <div class="top-bar-filters">
                                    <applied-filters
                                        :grouped-applied-filters="$parent.groupedAppliedFilters"
                                        :available-fields="$parent.availableFields"
                                        @add-dynamic-filter="$parent.addDynamicFilter($event)"
                                        @remove-generic-filter="$parent.removeGenericFilter($event)"
                                        @remove-filter="$parent.removeFilter($event)"
                                        @remove-dynamic-filter="$parent.removeDynamicFilter($event)"
                                        @remove-search-filter="$parent.clearSearchFilter($event)"
                                        @update-condition="$emit('update-condition', $event)"
                                    >
                                        <template v-slot:first>
                                            <div class="is-narrow">
                                                <div
                                                    class="button is-small"
                                                    @click="toggleDynamicFilter"
                                                >
                                                    <i
                                                        class="fa"
                                                        :class="!showDynamicFilter ? 'fa-plus' : 'fa-angle-up'"
                                                    ></i>
                                                </div>
                                                <dynamic-filter
                                                    v-if="showDynamicFilter"
                                                    :available-fields="$parent.availableFields"
                                                    :condition-name="$parent.groupedAppliedFilters.length ? 'And' : null"
                                                    @add="$parent.addDynamicFilter($event)"
                                                    @update-condition="$emit('update-condition', $event)"
                                                    @close="showDynamicFilter = false"
                                                />
                                            </div>
                                        </template>
                                    </applied-filters>
                                </div>
                                <slot name="top-bar-right"></slot>
                            </div>

                            <div class="flex-con is-grow sm-pad-lr">
                                <slot name="before-search"></slot>
                                <div>
                                    <div class="field has-addons">
                                        <div class="control is-expanded has-icons-left">
                                            <input
                                                class="input search-input"
                                                v-model="$parent.searchText"
                                                @keyup.enter="$parent.search()"
                                                placeholder="Search..."
                                            >
                                            <span class="icon is-small is-left">
                                                <i class="fa fa-search"></i>
                                            </span>
                                        </div>
                                        <div class="control">
                                            <search-selection
                                                :value="$parent.filters.searchColumns"
                                                :searchable-fields-groups="$parent.availableFields.searchableFieldsGroups"
                                                @input="$parent.setSearchColumns($event)"
                                                v-on:refresh="$parent.searchColumnsChanged()"
                                            >
                                                <template v-slot:default-search-modal-body>
                                                    <slot name="default-search-modal-body"></slot>
                                                </template>
                                            </search-selection>
                                        </div>
                                    </div>
                                </div>
                                <slot name="after-search"></slot>
                                <div class="is-narrow is-end">
                                    <pagination-controls
                                        :fetching="$parent.fetching"
                                        :total="$parent.data.total"
                                        :items-on-page="$parent.data.items.length"
                                        :limit="$parent.filters.limit"
                                        :offset="$parent.filters.offset"
                                        :limit-options="$parent.limitOptions"
                                        :estimated-total="$parent.estimatingTotalCount"
                                        :is-pagination-exact="$parent.isPaginationExact"
                                        :allow-multiple-pagination-styles="$parent.allowMultiplePaginationStyles"
                                        @previous-page="$parent.previousPage()"
                                        @next-page="$parent.nextPage()"
                                        @change-page="$parent.changePage($event)"
                                        @change-limit="$parent.changeLimit($event)"
                                        @type-modal-open="$parent.paginationModal.open"
                                        @reset-pagination-style="$parent.resetPaginationStyle"
                                    ></pagination-controls>
                                </div>
                                <div class="is-narrow" v-if="$parent.vuexSearchProjectionStore">
                                    <projection-selection
                                        :vuex-search-projection-store="$parent.vuexSearchProjectionStore"
                                        :selected-projection="$parent.selectedProjection"
                                        @change="$parent.applyProjection($event)"
                                        v-on:manage="$parent.projectionsModal.open()"
                                    >
                                    </projection-selection>
                                </div>
                            </div>

                        </div>
                    </template>

                    <slot name="items-table">
                        <items-table
                            v-if="$parent.mounted"
                            :fields="$parent.activeProjectionFields"
                            :fetching="$parent.fetching"
                            :items="$parent.data.items"
                            :disabled="$parent.disabled"
                            :available-grouped-fields="$parent.availableProjectionGroups"
                            :sort-column-handle="$parent.filters.sortColumnHandle"
                            :sort-descending="$parent.filters.sortDescending"
                            :bulk-action-open="$parent.selectedItems.length > 0"
                            :custom-table-row-render-component="$parent.customTableRowRenderComponent"
                            :custom-table-row-render-component-props="$parent.customTableRowRenderComponentProps"
                            :class="{ 'bulk-action-select-all-offset' : $parent.bulkActionShowSelectAll }"
                            @change-sort-column="$parent.changeSortColumn($event)"
                            @change-sort-direction="$parent.changeSortDirection($event)"
                            @add-projection-column="$parent.addProjectionColumn($event)"
                            @remove-projection-column="$parent.removeProjectionColumn($event)"
                            @move-projection-column="$parent.moveProjectionColumn($event)"
                            @row-click="$parent.onItemRowClick($event)"
                            @broadcast="$parent.onBroadcast($event)"
                        >
                            <template v-if="$parent.supportsBulkActions" v-slot:th-bulk-action>
                                <th
                                    class="action-col"
                                    scope="col"
                                    :class="{ 'bulk-actions-th' : $parent.selectedItems.length }"
                                    @click="$parent.toggleSelectAllItems(!$parent.selectedItems.length)"
                                >
                                    <div class="action-icon is-header" :class="{'active' : $parent.selectedItems.length }">
                                        <i
                                            v-if="$parent.selectedItems.length === $parent.filters.limit"
                                            class="fa fa-check"
                                            :class="$parent.selectedItems.length === $parent.filters.limit ? 'fa-check' : 'fa-minus'"
                                        ></i>
                                        <i v-else-if="$parent.selectedItems.length" class="fa fa-minus"></i>
                                    </div>
                                </th>
                                <th
                                    v-if="$parent.selectedItems.length"
                                    colspan="99"
                                    scope="col"
                                    class="bulk-actions-th"
                                >
                                    <div class="bulk-actions-con">
                                        <div class="selected-count-con">
                                            <span v-if="$parent.isBulkActionAll && $parent.fetchingTotal">
                                                Calculating total <i class="loading-dots"></i>
                                            </span>
                                            <span v-else>
                                                <number :value="$parent.bulkActionTotalItems()"/>
                                                selected
                                            </span>
                                            <span class="icon">
                                                <i class="fa fa-long-arrow-right"></i>
                                            </span>
                                        </div>
                                        <div
                                            v-for="(item,i) in $parent.bulkActionItems"
                                            :key="i"
                                            class="menu-item"
                                            :class="{ 'sub-menu-open' : item.subMenuOpen, 'hidden' : item.hide }"
                                            @click="$parent.onBulkActionMenuItemClicked(item)"
                                        >
                                            <div class="item">
                                                <span>
                                                    {{item.name}}
                                                </span>
                                                <span v-if="item.menuItems" class="icon">
                                                    <i class="fa fa-angle-down"></i>
                                                </span>
                                            </div>
                                            <div v-if="item.subMenuOpen" class="submenu">
                                                <div
                                                    v-for="(subItem, si) in item.menuItems"
                                                    :key="si"
                                                    @click="subItem.method"
                                                >
                                                    {{subItem.name}}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            v-if="!$parent.hideExport"
                                            class="menu-item"
                                            @click="$parent.onBulkActionExport()"
                                        >
                                            <div class="item">
                                                Export
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        class="bulk-actions-select-count-con"
                                        v-if="$parent.bulkActionShowSelectAll"
                                    >
                                        <div v-if="!$parent.isBulkActionAll && !$parent.fetchingTotal">
                                            All {{$parent.selectedItems.length}} {{$parent.bulkActionItemNoun}} on the page are selected.
                                            <span v-if="$parent.isBulkActionAll && $parent.fetchingTotal">
                                                Selecting all matching <i class="loading-dots"></i>
                                            </span>
                                            <a v-else @click="$parent.bulkActionSelectAll()">
                                                Select all
                                                <number v-if="$parent.data.total" :value="$parent.data.total"/>
                                                <span v-else>{{$parent.filters.limit}}+</span>
                                                {{$parent.bulkActionItemNoun}}
                                            </a>
                                        </div>
                                        <div v-else>
                                            All <number :value="$parent.data.total"/> {{$parent.bulkActionItemNoun}} are selected.
                                            <a @click="$parent.bulkActionDeselectAll()">
                                                Clear selection.
                                            </a>
                                        </div>
                                    </div>
                                </th>
                            </template>
                            <template v-if="$parent.supportsBulkActions" v-slot:tr-bulk-action="{item}">
                                <td
                                    class="action-col"
                                    @click="$parent.toggleItemSelection($event, item)"
                                >
                                    <div class="action-icon" :class="{'active' : $parent.itemIsSelected(item) }">
                                        <i class="fa fa-check"></i>
                                    </div>
                                </td>
                            </template>
                            <template v-slot:th-start v-if="!$parent.selectedItems.length">
                                <slot name="th-start"></slot>
                            </template>
                            <template v-slot:th-end v-if="!$parent.selectedItems.length">
                                <slot name="th-end"></slot>
                            </template>
                            <template v-slot:table-headers>
                                <slot name="table-headers"></slot>
                            </template>
                            <template v-slot:tr-start="{ item }">
                                <slot
                                    name="tr-start"
                                    v-bind:item="item"
                                ></slot>
                            </template>
                            <template v-slot:tr-end="{ item }">
                                <slot
                                    name="tr-end"
                                    v-bind:item="item"
                                ></slot>
                            </template>
                            <template v-slot:no-items>
                                <slot name="no-items"></slot>
                            </template>
                        </items-table>
                        <loading v-else></loading>
                    </slot>

                </scrollable-page-layout>

                <slot name="default"></slot>

                <modal
                    :active="$parent.filtersModal.active"
                    :close="$parent.filtersModal.close"
                    :is-extra-large="true"
                    :no-padding="true"
                >
                    <manage-filters
                        :vuex-search-filter-store="$parent.vuexSearchFilterStore"
                        :available-fields="$parent.availableFields"
                        v-on:filter-updated="$parent.filtersModal.changedIds.push($event)"
                        v-on:filter-deleted="$parent.filtersModal.deletedIds.push($event)"
                    ></manage-filters>
                </modal>

                <modal
                    :active="$parent.projectionsModal.active"
                    :close="$parent.projectionsModal.close"
                    :is-extra-large="true"
                    :no-padding="true"
                >
                    <manage-projections
                        :vuex-search-projection-store="$parent.vuexSearchProjectionStore"
                        :available-fields="$parent.availableProjectionFields"
                        :available-grouped-fields="$parent.availableProjectionGroups"
                        v-on:projection-updated="$parent.projectionsModal.changedIds.push($event)"
                        v-on:projection-deleted="$parent.projectionsModal.deletedIds.push($event)"
                    ></manage-projections>
                </modal>

                <confirm-modal
                    :active="$parent.confirmActionModal.active"
                    :loading="$parent.confirmActionModal.loading"
                    :close="() => { $parent.confirmActionModal.active = false }"
                    :callback="$parent.confirmActionModal.callback"
                    :callback-on-yes-only="true"
                >
                    <div class="has-text-centered">
                        <h5 class="is-size-5">{{$parent.confirmActionModal.text}}</h5>
                        <div v-html="$parent.confirmActionModal.subText"></div>
                    </div>
                </confirm-modal>


                <modal
                    :active="$parent.paginationModal.active"
                    :close="$parent.paginationModal.close"
                    is-card
                >
                    <template #header>
                        <h5 class="heading is-size-5">
                            Pagination Style
                        </h5>
                    </template>

                    <template #default>
                        <radio-buttons
                            v-model="$parent.paginationModal.originalValue"
                            :options="$parent.paginationOptions"
                        >
                        </radio-buttons>
                        <div v-if="!$parent.isPaginationExact">
                            <hr>
                            <div
                                v-if="$parent.paginationModal.currentTotal === null"
                                class="flex-con xs-pad-lr"
                            >
                                <div>
                                    <loading-button
                                        :loading="$parent.paginationModal.fetchingTotal"
                                        @click="$parent.paginationModal.fetchTotal"
                                    >
                                        Calculate Total
                                    </loading-button>
                                </div>
                                <div>Use the button on the left to calculate the total items in current view</div>
                            </div>
                            <div
                                v-else
                                class="flex-con xs-pad-lr"
                            >
                                <div>
                                    <number
                                        class="has-text-weight-bold is-size-6"
                                        :value="$parent.paginationModal.currentTotal"
                                    />
                                </div>
                                <div>Total items in current view</div>
                            </div>
                        </div>
                    </template>

                    <template #footer>
                        <div class="flex-con is-grow sm-pad-lr">
                            <div>
                                <div class="button" @click="$parent.paginationModal.close">
                                    Cancel
                                </div>
                            </div>
                            <div class="is-narrow">
                                <div class="button is-primary" @click="$parent.paginationModal.apply">
                                    Apply
                                </div>
                            </div>
                        </div>
                    </template>

                </modal>

            </template>

        </side-menu-layout>

        <slot name="outside-of-container"></slot>

        <modal :active="$parent.fetchingTotalForValidationModal">
            <loading/>
        </modal>

    </div>
</template>

<script>
import ItemsTable from './components/ItemsTable';


import AppliedFilters from './components/AppliedFilters';
import DynamicFilter from './components/DynamicFilter';
import PaginationControls from './components/PaginationControls';
import SearchSelection from './components/SearchSelection';
import EventBus from '$flx/event-bus';
export default {
    name : "AbstractSearch",
    components : {
        ItemsTable,
        AppliedFilters,
        DynamicFilter,
        PaginationControls,
        SearchSelection,
    },
    data () {
        return {
            showDynamicFilter : false,
        };
    },
    beforeMount () {
        EventBus.on(EventBus.EVENT.ABSTRACT_SEARCH_DYNAMIC_FILTER_OPEN, (context) => {
            if(context.fromApplied) {
                this.showDynamicFilter = false;
            }
        });
    },
    methods : {
        toggleDynamicFilter () {
            if(!this.showDynamicFilter) {
                EventBus.emit(EventBus.EVENT.ABSTRACT_SEARCH_DYNAMIC_FILTER_OPEN, {fromApplied : false});
            }
            this.showDynamicFilter = !this.showDynamicFilter;
        },
    },
}
</script>

<style lang="scss">
    @import './css/abstract-search';
</style>
